// @flow

import React, { useEffect, useRef, useState } from "react";
import { localize } from "redux-i18n";
import { SchemaForm, Button, Text, View } from "@components";

import { split } from "@transformers/schema";
import type { PropertiesSchema } from "@types";
import type { ReportingParametersData } from "@modules/service/reducer/reporting-parameters/types";
import uiSchema from "./ui-schema";

type Props = $Exact<{
  clientId: ?number,
  data: ReportingParametersData,
  schema: PropertiesSchema,
  updateReportingParameters: Function,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  edit: boolean,
}>;

const DEFAULT_FORM_VALUES = {
  reportingPeriods: "CALENDAR_MONTH",
  salaryCostAbsenceTrend: false,
  employeeAbsenceRates: true,
  rtwIncludedAbsenceRates: true,
};

const BradfordScores = props => {
  const formRef = useRef(null);
  const [edit, setEdit] = useState(false);
  const [formData, setFormData] = useState<ReportingParametersData>(DEFAULT_FORM_VALUES);

  useEffect(() => {
    setFormData({
      ...props.data,
      ...DEFAULT_FORM_VALUES,
    });
  }, [props.data]);

  const toggleEdit = () => {
    setEdit(prev => !prev);
  };

  const cancelEdit = () => {
    setEdit(false);
    setFormData({
      ...props.data,
      ...DEFAULT_FORM_VALUES,
    });
  };

  const submitForm = () => {
    formRef.current.submit();
  };

  const handleFormChange = (formData: ReportingParametersData) => {
    setFormData(formData);
  };

  const handleSubmit = (formData: Object) => {
    const data = {
      ...formData,
      bv12_trend: formData.bv12Trend, // snakecase library we use does not properly handles this case
    };
    delete data.bv12Trend;
    props.updateReportingParameters({
      clientId: props.clientId,
      formData: data,
    });
    toggleEdit();
  };

  const getUiSchema = () => {
    return uiSchema(props.t, formData.bradfordScore, edit);
  };

  const schema = () => {
    return split(props.schema, [
      "bradfordScore",
      "bradfordScoreAbsenceType",
      "bradfordScoreDayType",
      "bradfordScorePeriod",
    ]);
  };

  return (
    <View className="gs-container" flex={1} gap={6}>
      <View direction="row" justifyContent="space-between">
        <Text type="h6">{props.t("BRADFORD_SCORES")}</Text>
        <View direction="row" justifyContent="space-between" gap="4">
          {!edit ? (
            <Button materialIcon="edit" onClick={toggleEdit} size="small">
              {props.t("EDIT")}
            </Button>
          ) : (
            <View direction="row" gap="4">
              <Button color="secondary" onClick={cancelEdit} size="small">
                {props.t("CANCEL")}
              </Button>
              <Button
                materialIcon="replay_circle_filled"
                className="ml-2"
                onClick={submitForm}
                size="small"
              >
                {props.t("SUBMIT")}
              </Button>
            </View>
          )}
        </View>
      </View>
      <SchemaForm
        ref={formRef}
        schema={schema()}
        formData={formData}
        onSubmit={handleSubmit}
        onChange={handleFormChange}
        uiSchema={getUiSchema()}
        readonly={!edit}
        externalSubmit
        boxGrid
      />
    </View>
  );
};

export default localize()(BradfordScores);
